import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Globe, ChevronDown } from 'lucide-react';

const Header = () => {
  const { t } = useTranslation();
  const { isRTL, currentLang, toggleLanguage } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.about'), path: '/about' },
    { name: t('nav.projects'), path: '/projects' },
    { name: t('nav.blog'), path: '/blog' },
    { name: t('nav.contact'), path: '/contact' },
  ];

  return (
    <>
      <header
        data-testid="main-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled 
            ? 'glass shadow-lg py-3' 
            : 'bg-transparent py-5'
        }`}
      >
        <div className="container-custom flex items-center justify-between">
          {/* Logo */}
          <Link 
            to="/" 
            data-testid="logo-link"
            className="flex items-center gap-3"
          >
            <span className={`text-2xl md:text-3xl font-bold tracking-tight ${
              isScrolled ? 'text-foreground' : 'text-white'
            }`}>
              ARMAN
            </span>
            <span className={`text-gold text-sm uppercase tracking-[0.3em] ${
              isScrolled ? '' : 'text-gold'
            }`}>
              Kitchens
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.path.replace('/', '') || 'home'}`}
                className={`text-sm uppercase tracking-wider transition-colors duration-300 hover:text-gold ${
                  location.pathname === link.path 
                    ? 'text-gold' 
                    : isScrolled ? 'text-foreground' : 'text-white'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* Right Side */}
          <div className="flex items-center gap-4">
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              data-testid="language-toggle"
              className={`flex items-center gap-2 px-3 py-2 text-sm transition-colors duration-300 hover:text-gold ${
                isScrolled ? 'text-foreground' : 'text-white'
              }`}
            >
              <Globe size={18} />
              <span>{currentLang === 'ar' ? 'EN' : 'عربي'}</span>
            </button>

            {/* CTA Button - Desktop */}
            <Link
              to="/contact"
              data-testid="header-cta"
              className="hidden md:block bg-gold text-charcoal px-6 py-3 text-sm uppercase tracking-wider font-medium hover:bg-gold/90 transition-colors duration-300"
            >
              {t('nav.getQuote')}
            </Link>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(true)}
              data-testid="mobile-menu-btn"
              className={`lg:hidden p-2 ${
                isScrolled ? 'text-foreground' : 'text-white'
              }`}
            >
              <Menu size={24} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-brand-dark z-50 lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="container-custom py-5">
              <div className="flex items-center justify-between mb-12">
                <span className="text-2xl font-bold text-white">ARMAN</span>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="close-mobile-menu"
                  className="text-white p-2"
                >
                  <X size={24} />
                </button>
              </div>

              <nav className="flex flex-col gap-6">
                {navLinks.map((link, i) => (
                  <motion.div
                    key={link.path}
                    initial={{ opacity: 0, x: isRTL ? 20 : -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.1 }}
                  >
                    <Link
                      to={link.path}
                      data-testid={`mobile-nav-${link.path.replace('/', '') || 'home'}`}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`text-2xl text-white hover:text-gold transition-colors ${
                        location.pathname === link.path ? 'text-gold' : ''
                      }`}
                    >
                      {link.name}
                    </Link>
                  </motion.div>
                ))}
              </nav>

              <div className="mt-12 pt-8 border-t border-white/10">
                <button
                  onClick={() => {
                    toggleLanguage();
                    setMobileMenuOpen(false);
                  }}
                  className="flex items-center gap-3 text-white mb-6"
                >
                  <Globe size={20} />
                  <span>{currentLang === 'ar' ? 'English' : 'العربية'}</span>
                </button>

                <Link
                  to="/contact"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block w-full bg-gold text-charcoal text-center py-4 text-lg font-medium"
                >
                  {t('nav.getQuote')}
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
